/**
 * Tela /products/:productId (TH-53224 / M2.E8.S2.2).
 *
 * Substitui o stub registrado por TH-53223. Tabs:
 * - Visão geral: campos do produto + ações Editar / Deletar
 * - Preços: preço vigente por loja (1 linha por store) + botão "Lançar
 *   preço manual" abrindo NewPriceDialog
 * - Histórico: todas as linhas de store_prices ordenadas por valid_from DESC
 *
 * Backend: GET /api/products/{id} retorna ProductRead. GET
 * /api/products/{id}/prices retorna `StorePriceRead[]` com `is_current`
 * já calculado server-side. POST /api/products/{id}/prices fecha o
 * vigente atual e cria nova linha (append-only).
 *
 * 404 → empty state amigável; deletar → ConfirmDialog → navega para /products.
 */

import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";
import { ConfirmDialog } from "@/pages/tv-detail/ConfirmDialog";
import { NewPriceDialog } from "@/pages/product-detail/NewPriceDialog";
import { ProductEditDrawer } from "@/pages/product-detail/ProductEditDrawer";
import { cn } from "@/lib/utils";

interface Product {
  id: string;
  sku: string | null;
  ean: string | null;
  name: string;
  description: string | null;
  unit: string | null;
  image_url: string | null;
  ai_hint: string | null;
  area_id: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface StorePrice {
  id: string;
  store_id: string;
  price: string; // Decimal serializado
  promo_price: string | null;
  valid_from: string;
  valid_until: string | null;
  source: string | null;
  is_current: boolean;
  created_at: string;
}

interface Area {
  id: string;
  name: string;
}

interface Store {
  id: string;
  name: string;
}

type Tab = "overview" | "prices" | "history";

const dateTimeFmt = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

const priceFmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

async function fetchProduct(id: string): Promise<Product> {
  const res = await fetch(`/api/products/${id}`, { credentials: "include" });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return (await res.json()) as Product;
}

async function fetchPrices(productId: string): Promise<StorePrice[]> {
  const res = await fetch(`/api/products/${productId}/prices`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as StorePrice[];
}

async function fetchAreas(): Promise<Area[]> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch(`/api/areas?per_page=100`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as { data: Area[] };
  return body.data;
}

async function fetchStores(): Promise<Store[]> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch(`/api/stores?per_page=100`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as { data: Store[] };
  return body.data;
}

async function deleteProduct(id: string): Promise<void> {
  const res = await fetch(`/api/products/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

function formatPrice(v: string | null): string {
  if (v === null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return priceFmt.format(n);
}

export function ProductDetailPage() {
  const { productId } = useParams({ from: "/products/$productId" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canManage =
    user?.role === "super_admin" ||
    user?.role === "tenant_admin" ||
    user?.role === "operator" ||
    user?.role === "catalog_operator";

  const [tab, setTab] = useState<Tab>("overview");
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newPrice, setNewPrice] = useState(false);

  const productQ = useQuery({
    queryKey: ["product", productId],
    queryFn: () => fetchProduct(productId),
    staleTime: 30_000,
    enabled: !!user && canManage,
    retry: (failureCount, err) => {
      const status = (err as Error & { status?: number }).status;
      if (status === 404 || status === 403) return false;
      return failureCount < 2;
    },
  });

  const pricesQ = useQuery({
    queryKey: ["product-prices", productId],
    queryFn: () => fetchPrices(productId),
    staleTime: 30_000,
    enabled: !!user && canManage && !!productQ.data,
  });

  const areasQ = useQuery({
    queryKey: ["areas", "all"],
    queryFn: fetchAreas,
    staleTime: 5 * 60_000,
    enabled: !!user && canManage,
  });

  const storesQ = useQuery({
    queryKey: ["stores", "all"],
    queryFn: fetchStores,
    staleTime: 5 * 60_000,
    enabled: !!user && canManage,
  });

  const areaName = useMemo(() => {
    if (!productQ.data?.area_id) return null;
    return (
      areasQ.data?.find((a) => a.id === productQ.data?.area_id)?.name ?? null
    );
  }, [areasQ.data, productQ.data]);

  const storeNameById = useMemo(
    () => new Map((storesQ.data ?? []).map((s) => [s.id, s.name])),
    [storesQ.data],
  );

  // Para a aba "Preços" — última linha vigente por store.
  const currentByStore = useMemo(() => {
    if (!pricesQ.data) return [];
    const seen = new Set<string>();
    const out: StorePrice[] = [];
    for (const row of pricesQ.data) {
      if (seen.has(row.store_id)) continue;
      seen.add(row.store_id);
      out.push(row);
    }
    return out;
  }, [pricesQ.data]);

  const product = productQ.data;
  const isNotFound =
    productQ.isError &&
    (productQ.error as Error & { status?: number }).status === 404;

  const title = product?.name ?? (productQ.isLoading ? "Carregando…" : "Produto");
  const breadcrumb = product
    ? `OPERAÇÃO / PRODUTOS / ${(product.sku ?? product.name).toUpperCase()}`
    : "OPERAÇÃO / PRODUTOS";

  const headerActions = product ? (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setEditing(true)}
        disabled={!canManage}
      >
        Editar
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="text-[var(--color-destructive)]"
        onClick={() => setConfirmDelete(true)}
        disabled={!canManage}
      >
        Deletar
      </Button>
    </div>
  ) : null;

  // Acesso negado por role
  if (user && !canManage) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="OPERAÇÃO / PRODUTOS" title="Acesso negado">
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin,
              operator ou catalog_operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  return (
    <RequireAuth>
      <AppLayout breadcrumb={breadcrumb} title={title} actions={headerActions}>
        <div className="mx-auto max-w-5xl">
          {productQ.isLoading ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              carregando…
            </p>
          ) : isNotFound ? (
            <Card className="text-center">
              <h2 className="text-lg font-semibold">Produto não encontrado</h2>
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                O produto pode ter sido removido ou pertencer a outro tenant.
              </p>
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: "/products" })}
                >
                  Voltar para Produtos
                </Button>
              </div>
            </Card>
          ) : productQ.error ? (
            <p className="text-sm text-[var(--color-destructive)]">
              erro ao carregar produto
            </p>
          ) : product ? (
            <>
              {/* Header card */}
              <Card className="flex gap-4">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt=""
                    className="h-24 w-24 shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="flex h-24 w-24 shrink-0 items-center justify-center rounded-md bg-[var(--color-secondary)] text-xs text-[var(--color-muted-foreground)]"
                  >
                    sem imagem
                  </div>
                )}
                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-mono text-[var(--color-muted-foreground)]">
                      SKU: {product.sku ?? "—"}
                    </span>
                    {product.ean ? (
                      <span className="font-mono text-[var(--color-muted-foreground)]">
                        EAN: {product.ean}
                      </span>
                    ) : null}
                    <span
                      className={cn(
                        "rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                        product.active
                          ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                          : "bg-[var(--color-secondary)] text-[var(--color-muted-foreground)]",
                      )}
                    >
                      {product.active ? "ativo" : "inativo"}
                    </span>
                  </div>
                  {product.description ? (
                    <p className="text-sm text-[var(--color-muted-foreground)]">
                      {product.description}
                    </p>
                  ) : null}
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    Atualizado em{" "}
                    <span className="font-mono">
                      {dateTimeFmt.format(new Date(product.updated_at))}
                    </span>
                  </p>
                </div>
              </Card>

              {/* Tabs */}
              <nav
                role="tablist"
                aria-label="Abas do produto"
                className="mt-6 border-b border-[var(--color-border)]"
              >
                {(["overview", "prices", "history"] as const).map((t) => (
                  <TabButton
                    key={t}
                    active={tab === t}
                    onClick={() => setTab(t)}
                    label={
                      t === "overview"
                        ? "Visão geral"
                        : t === "prices"
                          ? "Preços"
                          : "Histórico"
                    }
                  />
                ))}
              </nav>

              <div className="mt-6">
                {tab === "overview" ? (
                  <OverviewTab product={product} areaName={areaName} />
                ) : tab === "prices" ? (
                  <PricesTab
                    prices={currentByStore}
                    storeNameById={storeNameById}
                    loading={pricesQ.isLoading}
                    error={pricesQ.error as Error | null}
                    canManage={canManage}
                    onNewPrice={() => setNewPrice(true)}
                    hasStores={(storesQ.data?.length ?? 0) > 0}
                  />
                ) : (
                  <HistoryTab
                    prices={pricesQ.data ?? []}
                    storeNameById={storeNameById}
                    loading={pricesQ.isLoading}
                    error={pricesQ.error as Error | null}
                  />
                )}
              </div>
            </>
          ) : null}
        </div>

        {product ? (
          <>
            <ProductEditDrawer
              open={editing}
              onOpenChange={setEditing}
              product={product}
              areas={areasQ.data ?? []}
            />
            <NewPriceDialog
              open={newPrice}
              onOpenChange={setNewPrice}
              productId={product.id}
              stores={storesQ.data ?? []}
            />
            <ConfirmDialog
              open={confirmDelete}
              onOpenChange={setConfirmDelete}
              title="Deletar produto?"
              description={`O produto "${product.name}" será apagado permanentemente. Não é possível deletar quando há histórico de preços associado — nesse caso, desative em vez de deletar.`}
              confirmLabel="Deletar"
              destructive
              onConfirm={() => deleteProduct(product.id)}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ["products"] });
                navigate({ to: "/products", replace: true });
              }}
            />
          </>
        ) : null}
      </AppLayout>
    </RequireAuth>
  );
}

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
        active
          ? "border-[var(--color-primary)] text-[var(--color-foreground)]"
          : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
      )}
    >
      {label}
    </button>
  );
}

function OverviewTab({
  product,
  areaName,
}: {
  product: Product;
  areaName: string | null;
}) {
  return (
    <Card className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Nome" value={product.name} />
      <Field label="SKU" value={product.sku} mono />
      <Field label="EAN" value={product.ean} mono />
      <Field label="Unidade" value={product.unit} />
      <Field
        label="Área"
        value={
          product.area_id ? (areaName ?? product.area_id) : "— sem área —"
        }
      />
      <Field label="Status" value={product.active ? "Ativo" : "Inativo"} />
      <Field
        label="Criado em"
        value={dateTimeFmt.format(new Date(product.created_at))}
        mono
      />
      <Field
        label="Atualizado em"
        value={dateTimeFmt.format(new Date(product.updated_at))}
        mono
      />
      {product.description ? (
        <div className="sm:col-span-2">
          <p className="text-xs uppercase text-[var(--color-muted-foreground)]">
            Descrição
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm">
            {product.description}
          </p>
        </div>
      ) : null}
      {product.ai_hint ? (
        <div className="sm:col-span-2">
          <p className="text-xs uppercase text-[var(--color-muted-foreground)]">
            Dica para IA
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm">{product.ai_hint}</p>
        </div>
      ) : null}
    </Card>
  );
}

function PricesTab({
  prices,
  storeNameById,
  loading,
  error,
  canManage,
  onNewPrice,
  hasStores,
}: {
  prices: StorePrice[];
  storeNameById: Map<string, string>;
  loading: boolean;
  error: Error | null;
  canManage: boolean;
  onNewPrice: () => void;
  hasStores: boolean;
}) {
  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Preços vigentes por loja</h2>
        {canManage ? (
          <Button
            onClick={onNewPrice}
            disabled={!hasStores}
            title={!hasStores ? "Nenhuma loja cadastrada" : undefined}
          >
            + Lançar preço manual
          </Button>
        ) : null}
      </div>

      <div className="mt-4">
        <DataTable<StorePrice>
          columns={[
            {
              key: "store_id",
              header: "Loja",
              cell: (p) => (
                <span className="font-medium">
                  {storeNameById.get(p.store_id) ?? p.store_id}
                </span>
              ),
            },
            {
              key: "price",
              header: "Preço regular",
              mono: true,
              align: "right",
              cell: (p) => formatPrice(p.price),
            },
            {
              key: "promo_price",
              header: "Preço promocional",
              mono: true,
              align: "right",
              cell: (p) => (p.promo_price ? formatPrice(p.promo_price) : "—"),
            },
            {
              key: "valid_from",
              header: "Vigente desde",
              mono: true,
              cell: (p) => (
                <span className="text-[var(--color-muted-foreground)]">
                  {dateTimeFmt.format(new Date(p.valid_from))}
                </span>
              ),
            },
            {
              key: "source",
              header: "Origem",
              cell: (p) => (
                <span className="text-[var(--color-muted-foreground)]">{p.source ?? "—"}</span>
              ),
            },
            {
              key: "is_current",
              header: "Status",
              align: "right",
              cell: (p) => (
                <StatusPill tone={p.is_current ? "success" : "muted"}>
                  {p.is_current ? "Vigente" : "Encerrado"}
                </StatusPill>
              ),
            },
          ] satisfies DataTableColumn<StorePrice>[]}
          rows={loading || error ? [] : prices}
          rowKey={(p) => p.id}
          emptyMessage={
            loading
              ? "carregando…"
              : error
                ? "erro ao carregar preços"
                : "nenhum preço cadastrado para este produto"
          }
        />
      </div>
    </section>
  );
}

function HistoryTab({
  prices,
  storeNameById,
  loading,
  error,
}: {
  prices: StorePrice[];
  storeNameById: Map<string, string>;
  loading: boolean;
  error: Error | null;
}) {
  return (
    <section>
      <h2 className="text-lg font-semibold">Histórico completo</h2>
      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
        Todas as entradas de preço, mais recentes primeiro. Append-only.
      </p>

      <div className="mt-4">
        <DataTable<StorePrice>
          columns={[
            {
              key: "store_id",
              header: "Loja",
              cell: (p) => (
                <span className="font-medium">
                  {storeNameById.get(p.store_id) ?? p.store_id}
                </span>
              ),
            },
            {
              key: "price",
              header: "Preço regular",
              mono: true,
              align: "right",
              cell: (p) => formatPrice(p.price),
            },
            {
              key: "promo_price",
              header: "Preço promocional",
              mono: true,
              align: "right",
              cell: (p) => (p.promo_price ? formatPrice(p.promo_price) : "—"),
            },
            {
              key: "valid_from",
              header: "Vigente de",
              mono: true,
              cell: (p) => (
                <span className="text-[var(--color-muted-foreground)]">
                  {dateTimeFmt.format(new Date(p.valid_from))}
                </span>
              ),
            },
            {
              key: "valid_until",
              header: "Vigente até",
              mono: true,
              cell: (p) => (
                <span className="text-[var(--color-muted-foreground)]">
                  {p.valid_until
                    ? dateTimeFmt.format(new Date(p.valid_until))
                    : "—"}
                </span>
              ),
            },
            {
              key: "source",
              header: "Origem",
              cell: (p) => (
                <span className="text-[var(--color-muted-foreground)]">{p.source ?? "—"}</span>
              ),
            },
          ] satisfies DataTableColumn<StorePrice>[]}
          rows={loading || error ? [] : prices}
          rowKey={(p) => p.id}
          emptyMessage={
            loading
              ? "carregando…"
              : error
                ? "erro ao carregar histórico"
                : "sem histórico de preços"
          }
        />
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-xs uppercase text-[var(--color-muted-foreground)]">
        {label}
      </p>
      <p className={cn("mt-1 text-sm", mono ? "font-mono text-xs" : "")}>
        {value ?? "—"}
      </p>
    </div>
  );
}
